import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";

interface SaveBarProps {
  isVisible: boolean;
  onSave: () => Promise<void>;
  onCancel: () => void;
  hasChanges: boolean;
}

export default function SaveBar({ isVisible, onSave, onCancel, hasChanges }: SaveBarProps) {
  const [isSaving, setIsSaving] = useState(false);
  const { toast } = useToast();

  const handleSave = async () => {
    if (!hasChanges) return;

    setIsSaving(true);
    try {
      await onSave();
      toast({
        title: "Success",
        description: "Settings saved successfully",
      });
    } catch (error) {
      console.error("Error saving settings:", error);
      toast({
        title: "Error",
        description: "Failed to save settings",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-background border-t border shadow-lg z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center space-x-4">
            <div className="text-sm text-gray-600">
              {hasChanges ? "You have unsaved changes" : "No changes to save"}
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <Button
              onClick={onCancel}
              variant="ghost"
              disabled={isSaving}
            >
              Cancel
            </Button>

            <Button
              onClick={handleSave}
              disabled={!hasChanges || isSaving}
              variant="default"

            >
              {isSaving ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
